/**
 * Голая нога: первая ставка стоит, вторая не встала.
 *
 * До сих пор это состояние в продукте не называлось. Карточка писала «первая
 * нога стоит одна, план закрытия ниже» — и ниже ничего не было. Между тем это
 * единственное состояние, в котором человек уже потерял выбор: вилки нет, есть
 * обычная ставка на весь размер ноги, и решать надо сейчас, а не «когда
 * появится время».
 *
 * Считаем ровно три числа, и все три — про деньги, а не про проценты.
 *
 * 1. **Чем это кончится, если ничего не делать.** Ставка либо заходит, либо
 *    нет: плюс `payout − stake` или минус `stake`. Не «риск 3 200 ₽», а два
 *    исхода, между которыми человек и выбирает.
 * 2. **Кэф спасения.** Ниже него закрытие второй ногой уже убыточно. Выводится
 *    из условия «выплата покрывает обе ставки»: `K₂ ≥ K₁ / (K₁ − 1)`. Число
 *    маленькое и считается в уме за пять секунд — но не в тот момент, когда на
 *    кону деньги, поэтому оно стоит на экране.
 * 3. **Сколько зафиксируется убытка**, если закрыть по тому кэфу, который есть
 *    сейчас. Ноль означает, что вилка ещё цела, просто похудела.
 *
 * Совета «закрывай» или «держи» здесь нет и не будет: это ставка человека и
 * его деньги. Наше дело — назвать числа, которых в этот момент нет ни у кого.
 */

import { Decimal } from 'decimal.js';

import type { RowLeg } from '../lib/row';

export interface Naked {
  /** Нога, которая стоит. */
  stood: RowLeg;
  /** Нога, которой не хватает. */
  left: RowLeg;
  /** Сколько уже на кону, ₽. */
  risk: number;
  /** Что будет, если не закрывать: плюс при заходе, минус при проигрыше. */
  win: number;
  /** Кэф, ниже которого закрытие уже в убыток. */
  save: number;
  /** Кэф второй ноги прямо сейчас. */
  now: number;
  /** Сколько ставить второй ногой, чтобы сравнять исходы, ₽. */
  stake: number;
  /** Убыток, который зафиксируется закрытием по нынешнему кэфу. 0 — вилка цела. */
  loss: number;
}

/**
 * Собрать состояние по отметкам карточки.
 *
 * Только для двуногих вилок: у трёхногой «голая нога» — это две недостающие
 * стороны и другой разговор, а выдумывать для неё план закрытия по одной
 * формуле значило бы показать число, которое не проверено ничем.
 */
export function nakedOf(
  legs: readonly RowLeg[],
  placed: readonly (number | null)[],
  failed: readonly boolean[],
): Naked | null {
  if (legs.length !== 2) return null;
  const first = placed[0] !== null;
  const second = placed[1] !== null;
  if (first === second) return null;
  const at = first ? 0 : 1;
  const other = first ? 1 : 0;
  // Пока человек ещё в дороге ко второй ноге, это не голая нога, а обычная
  // середина входа. Состояние объявляется только когда он сам сказал «не
  // вышло»: до этого показывать план закрытия значит торопить его бросить
  // вилку, которая ещё встанет.
  if (failed[other] !== true) return null;

  const stood = legs[at]!;
  const left = legs[other]!;
  const k1 = new Decimal(stood.odds);
  const k2 = new Decimal(left.odds);
  if (!k1.gt(1) || !k2.gt(0)) return null;

  const risk = new Decimal(stood.rub);
  const payout = risk.times(k1);
  const save = k1.div(k1.minus(1));

  // Сумма, при которой оба исхода дают одно и то же: S₂ = payout / K₂.
  const stake = payout.div(k2);
  const back = stake.times(k2).minus(risk).minus(stake);
  return {
    stood,
    left,
    risk: money(risk),
    win: money(payout.minus(risk)),
    save: Number(save.toFixed(3)),
    now: Number(k2.toFixed(3)),
    stake: money(stake),
    // Убыток называем положительным числом: «зафиксируете 240 ₽ убытка»
    // читается быстрее, чем «−240».
    loss: back.gte(0) ? 0 : money(back.abs()),
  };
}

const money = (value: Decimal): number => Number(value.toDecimalPlaces(0, Decimal.ROUND_HALF_UP));

/** Строка для карточки. Одна, потому что читают её в неудачную минуту. */
export function nakedText(naked: Naked): string {
  if (naked.loss === 0) {
    return `вилка цела: закройте ${naked.stake} ₽ по кэфу ${naked.now}. прибыль меньше, но она есть`;
  }
  return `закрытие по ${naked.now} зафиксирует ${naked.loss} ₽ убытка; без убытка нужен кэф от ${naked.save}`;
}
